import { VaultDB } from './journal/vault-db.js';
export { Microkernel } from './core/microkernel.js';
export { PluginRegistry } from './core/plugin-registry.js';
export { WhiteboardPlugin } from './plugins/whiteboard/plugin.js';
export { ChatPlugin } from './plugins/chat/plugin.js';
export { DefaultMeshBus } from './transport/mesh-bus.js';
export { InMemoryLink } from './transport/memory-link.js';
export { BroadcastChannelLink } from './transport/broadcast-channel-link.js';
export { WebSocketLinkAdapter } from './transport/websocket-link.js';
export { Deduplicator } from './transport/deduplicator.js';
export { InMemoryEnvelopeJournal } from './journal/envelope-journal.js';
export { SyncService } from './sync/sync-service.js';
export { MeshAdapter } from './react/adapter.js';
export { MeshProvider, useMeshState } from './react/hooks.js';
export { parseHtsUri, parseHnsUri } from './validation/uri-validation.js';
export { parsePluginNamespace, validateEnvelope } from './validation/envelope-validation.js';
export { parseEventId } from './validation/uuid-validation.js';
export { SecurityTier } from './types/security.js';
export { DirectoryService, createBroadcastDescriptor } from './host/directory-service.js';
export { BroadcastService } from './host/broadcast-service.js';
export { InviteService } from './host/invite-service.js';
export { AppHostContainerBridge } from './host/app-bridge.js';
export type { HostDirectoryService, HostBroadcastService, HostInviteService, BridgePort, Result, HostSDKError, PeerDescriptor, BroadcastDescriptor, ChannelDescriptor, IncomingInvite, DirectoryChangeEvent } from './host/types.js';
export type { Stroke, Point, WhiteboardState, WhiteboardAction } from './plugins/whiteboard/types.js';
export type { ChatMessage, ChatState, ChatAction } from './plugins/chat/types.js';

// Phase 0 Agent System Contracts & Mocks
export * from './core/contracts/index.js';
export { MockRagProvider } from './mocks/mock-rag-provider.js';
export { MockLlmWikiProvider } from './mocks/mock-llmwiki-provider.js';
export { MockBuzzAdapter } from './mocks/mock-buzz-adapter.js';
export { MockSimulatorRuntime } from './mocks/mock-simulator-runtime.js';

// Phase 1 Identity & Task Routing Engine
export * from './identity/entity-id.js';
export * from './identity/agent-identity.js';
export { AgentRegistry } from './agents/agent-registry.js';
export { AuditLogger, type AuditRecord } from './agents/audit-log.js';
export { TaskRouter } from './agents/task-router.js';

// Phase 3 Knowledge Layer Expansion
export { RagKnowledgeProvider } from './plugins/rag/provider.js';
export { RagIndexer, type RagDocument, type RagChunk } from './plugins/rag/indexer.js';
export { LLMWikiKnowledgeProvider } from './plugins/llmwiki/provider.js';
export { LLMWikiCompiler } from './plugins/llmwiki/compiler.js';
export type { LLMWikiPage, LLMWikiPageStatus } from './plugins/llmwiki/page-schema.js';
export { HybridKnowledgeProvider } from './knowledge/hybrid-provider.js';

// Phase 4 Buzz/ACP Adapter & Tenancy
export { BuzzAcpRuntimeAdapter } from './plugins/buzz/adapter/acp-runtime.js';
export { BuzzAgentPlugin } from './plugins/buzz/plugin.js';
export type { AcpHeader, AcpMessageType, AcpTaskPayload, AcpMessageEnvelope } from './plugins/buzz/protocol/types.js';
export { getCapabilityRiskLevel, requiresHumanApproval } from './tenancy/tenant-policy.js';
export { loadTenantAgentPack, type DeclarativeAgentConfig, type TenantAgentPack } from './tenancy/agent-pack.js';

// Phase 5 Channel-Agent Orchestration & Kanban Proposals
export type * from './core/contracts/channel-agent.js';
export type * from './core/contracts/proposals.js';
export { ChannelBindingRegistry } from './agents/channel-binding-registry.js';
export { TriggerMatcher } from './agents/trigger-matcher.js';
export { ChannelAgentRouter } from './agents/channel-agent-router.js';
export { ScrumMasterWorkflow, type StandupResponse } from './agents/workflows/scrum-master-workflow.js';
export { InMemoryKanbanProvider, type KanbanProvider, type KanbanTask, type KanbanBoard } from './plugins/kanban/kanban-provider.js';
export { ActionPlannerEngine, type ActionPlannerViewMode, type ActionPlannerTaskExtension, type ExtendedKanbanTask, type WbsTreeNode, type PlannerQueryFilter } from './plugins/kanban/action-planner-engine.js';
export { ActionPlannerPlugin } from './plugins/action-planner/action-planner-plugin.js';
export { actionPlannerReducer } from './plugins/action-planner/action-planner-reducer.js';
export { LexoRankOrdering } from './plugins/action-planner/action-planner-ordering.js';
export { WbsHierarchyEngine, type WbsNode } from './plugins/action-planner/action-planner-wbs.js';
export { BoundedQueryParser, type BoundedQueryFilter } from './plugins/action-planner/action-planner-query.js';
export { AutomationCircuitBreaker } from './plugins/action-planner/action-planner-automation.js';
export { ActionPlannerTheme } from './plugins/action-planner/action-planner-theme.js';
export * from './plugins/action-planner/action-planner-types.js';
export { ProposalService } from './kanban/proposal-service.js';
export { ProposalExecutor, type ExecutionResult } from './kanban/proposal-executor.js';

// Phase 7 System Elements Integration
export { VaultDB, globalVaultDB, type StoredFile, type StoredMessage, type StoredIdentity, type VaultState } from './journal/vault-db.js';
export { SovereignIdentityRegistry, globalIdentityRegistry, type SovereignIdentityRecord } from './identity/identity-registry.js';
export { PluginActionBus, globalPluginActionBus, type PluginActionEvent } from './core/plugin-action-bus.js';
export { PluginCommandRegistry, globalCommandRegistry, type PluginCommandDefinition } from './core/plugin-commands.js';
export { NotificationStore, globalNotificationStore, type SystemNotification } from './plugins/notifications/notification-store.js';
export { WorkspaceStore, globalWorkspaceStore, type WorkspaceLayoutState } from './store/workspace-store.js';
export { KanbanCollabBridge, type KanbanCollabPayload } from './plugins/kanban/kanban-collab-bridge.js';
export { WhiteboardStore, globalWhiteboardStore } from './plugins/whiteboard/store.js';
export { broadcastIdentityUpdate, applyNetworkSettings, reconcileBackupPayload } from './security/settings-hardening.js';
export { SETTINGS_CATALOG, getSettingMetadata, type SettingMetadata, type UserRole } from './config/settings-catalog.js';

// Phase 9 Scoped RBAC Engine & Control Plane Journal
export * from './security/rbac-contracts';
export { ControlPlaneJournal, type JournalDependencies } from './security/control-plane-journal';
export { ScopedRbacEvaluator, requirePermission, type EvaluatorDependencies } from './security/rbac-evaluator';

// Sovereign Genesis Provisioning & Factory Reset Wizard Engine
export { generateUUIDv7, resetUuidV7State } from './identity/uuidv7';
export { GenesisProvisioner, deriveCryptographicMeshId, type OwnerBinding, type HnsBinding, type GenesisConfiguration } from './core/genesis-provisioner';

// Sovereign Mesh CLI Binary Installer & Lifecycle Engine (CLI/Node only, decoupled from browser barrel)
// export * from './cli/genesis-schema';
// export * from './cli/key-provider';
// export * from './cli/storage';
// export * from './cli/wizard';
// export * from './cli/status.js';
// export * from './cli/mesh-installer.js';

// Policy-Aware Multiprotocol Transport Engine
export * from './transports/transport-adapter-interface.js';
export * from './transports/fake-deterministic-adapter.js';
export * from './transports/bluetooth-mesh-adapter.js';
export * from './transports/multiprotocol-router.js';

// Agent Gateway & Needle 2 Schema Extractor Engine
export * from './agent-gateway/needle-worker.js';
export * from './agent-gateway/llmwiki-bridge.js';
export * from './plugins/scrum-master/scrum-workflow-types.js';
export * from './plugins/scrum-master/scrum-needle-extractor.js';
export * from './plugins/scrum-master/dependency-impact-engine.js';
export * from './ai/local-inference-provider.js';

// Sprint K-01 Curated Knowledge Engine (LLMWiki)
export * from './security/llmwiki-contracts.js';
export * from './security/secret-scrubber.js';
export * from './plugins/llmwiki/llmwiki-store.js';
export * from './plugins/llmwiki/indexing-outbox.js';
export * from './plugins/llmwiki/wiki-harvester.js';

// Sprint MSG-01 Sovereign Message Journaling & Persistence Engine
export * from './shell/chat/chat-contracts.js';
export { MessageRepository, globalMessageRepository } from './shell/chat/MessageRepository.js';
export { AttachmentRepository, globalAttachmentRepository } from './shell/chat/AttachmentRepository.js';
export { BackupRepository, globalBackupRepository } from './shell/chat/BackupRepository.js';
export { ChatIngestionService } from './shell/chat/ChatIngestionService.js';
export { MeshChannelMessageService } from './shell/chat/ChannelMessageService.js';
export { VaultInvalidationBus, globalInvalidationBus } from './journal/vault-invalidation-bus.js';

// Sprint DIST-02A.1 Governed Mesh Compute & Scatter-Gather Orchestrator
export * from './compute/task-contracts.js';
export * from './compute/task-identity.js';
export { CapabilityLeaseValidator, type ValidationResult } from './compute/lease-validator.js';
export { ExecutionReceiptVerifier, type ReceiptVerificationResult } from './compute/receipt-verifier.js';
export { GatherPolicyEngine, type GatherContext } from './compute/gather-policy.js';
export { ScatterGatherOrchestrator } from './compute/scatter-gather-orchestrator.js';
export { DataPlaneStreamer, type DataPlaneTransfer } from './compute/data-plane-streamer.js';
export { TaskRunnerRegistry } from './compute/task-runner-registry.js';
export { WorkerSandboxProcess, type SandboxExecutionResult } from './compute/worker-sandbox-process.js';
export { HelperTaskWorker } from './compute/helper-task-worker.js';
export { RemoteTaskDispatcher, type RemoteExecutionHandler } from './compute/remote-task-dispatcher.js';

// Sprint DIST-03 Helper Node Setup, Dual-Sovereign Pairing & Resource Governance
export * from './mesh/enrollment/enrollment-contracts.js';
export { EnrollmentParser } from './mesh/enrollment/enrollment-parser.js';
export { EnrollmentVerifier } from './mesh/enrollment/enrollment-verifier.js';
export { EnrollmentTicketService } from './mesh/enrollment/enrollment-ticket-service.js';
export { HelperEnrollmentStateMachine } from './mesh/enrollment/enrollment-state-machine.js';
export { LocalHelperPolicyStore, DEFAULT_HARDWARE_BUDGET } from './mesh/governance/local-helper-policy.js';
export { CapabilityIntersectionEngine } from './mesh/governance/capability-intersection.js';
export { HelperResourceGovernor } from './mesh/governance/helper-resource-governor.js';
// export { PortBindingManager } from './mesh/transport/port-binding.js';
export { TransportEnvelopeGuard, MAX_CONTROL_ENVELOPE_BYTES } from './mesh/transport/transport-guards.js';
// export { CoordinatorClient } from './mesh/transport/coordinator-client.js';
// export { SetupHelperCommandHandler } from './cli/setup-helper-command.js';

// Sprint DIST-02B.1 Blind Encrypted Storage Slices, Quota Lifecycle & Versioned KEK Hierarchy
export * from './storage/slice-contracts.js';
export { SliceCryptoEnvelope, type EncryptSliceInput } from './storage/slice-crypto-envelope.js';
export { KekRotationService, type VersionedKek } from './storage/kek-rotation-service.js';
export { StorageReservationStore, type CreateReservationInput } from './storage/storage-reservation-store.js';
export { LocalSliceStore, type StoreSliceOptions } from './storage/local-slice-store.js';
export { HelperStorageService } from './storage/helper-storage-service.js';





// ─── Global VaultDB Binding for CRM / Storage ──────────────────────────
if (typeof (globalThis as any).VaultDB === 'undefined') {
  (globalThis as any).VaultDB = VaultDB;
}
